import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle Hilfe-Tour fuer den ABTEILUNGEN-Tab der Seite "Organisation &
 * Struktur" (`organization-overview`). Wird ueber den tab-bewussten
 * `<app-dialog-help-button>` in der `organization-header`-Toolbar gestartet
 * (Pattern analog `EMPLOYEE_LIST_HELP_TOUR`).
 *
 * Erklaerend (nextCondition 'manual', Spotlight + Voice-Over). Gibt den
 * fachlichen Ueberblick: die drei Bereiche (Abteilungen/Qualifikationen/
 * Organigramm), das Anlegen, die Tabellen-Spalten, die Zeilen-Aktionen und das
 * "Warum" (Abteilung + benoetigte Qualifikationen sind die Grundlage der
 * Personalbedarfs-Planung).
 *
 * `onBeforeShow` am Welcome-Step sichert defensiv den Abteilungen-Tab (der
 * `?`-Button erscheint zwar nur auf diesem Tab, aber Replays sollen robust
 * sein) — Mechanik analog `FIRST_DEPARTMENT_ONBOARDING_TOUR`.
 *
 * Cue-Daten (Tabs + Tabellen-Spalten) leben im Voice-Over-Manifest unter
 * `org-departments-help`; `hasCues` wird zur Laufzeit gesetzt.
 */
function ensureDepartmentsTab(): Promise<void> {
  const tab = document.querySelector<HTMLElement>('[onboardingAnchor="departments-tab"]');
  if (tab && !tab.classList.contains('is-active')) {
    tab.click();
    return new Promise<void>((resolve) =>
      requestAnimationFrame(() => resolve()),
    );
  }
  return Promise.resolve();
}

export const ORG_DEPARTMENTS_HELP_TOUR: TourConfig = {
  id: TourFeature.OrgDepartmentsHelp,
  version: 1,
  title: 'ORG_DEPARTMENTS_HELP_TOUR_TITLE',
  description: 'ORG_DEPARTMENTS_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  triggerContext: {
    route: '/organization',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 110,
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'ORG_DEPARTMENTS_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          anchor: AnchorId.OrganizationView,
          spotlightAnchor: AnchorId.OrganizationView,
          title: 'TUTORIAL.ORG_DEPARTMENTS.WELCOME.TITLE',
          content: 'TUTORIAL.ORG_DEPARTMENTS.WELCOME.CONTENT',
          placement: 'bottom',
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
          onBeforeShow: ensureDepartmentsTab,
        },
        {
          id: 'tabs',
          // Cues pulsen die drei Tab-Buttons (Manifest): Abteilungen,
          // Qualifikationen, Organigramm.
          anchor: AnchorId.OrganizationView,
          spotlightAnchor: AnchorId.OrganizationView,
          title: 'TUTORIAL.ORG_DEPARTMENTS.TABS.TITLE',
          content: 'TUTORIAL.ORG_DEPARTMENTS.TABS.CONTENT',
          placement: 'bottom',
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'add',
          anchor: AnchorId.AddDepartmentButton,
          spotlightAnchor: AnchorId.AddDepartmentButton,
          title: 'TUTORIAL.ORG_DEPARTMENTS.ADD.TITLE',
          content: 'TUTORIAL.ORG_DEPARTMENTS.ADD.CONTENT',
          placement: 'bottom',
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="add-department-button"]'),
        },
        {
          id: 'table',
          // Cues pulsen die Spaltenkoepfe (Manifest): Unterabteilungen,
          // Qualifikationen, Team.
          anchor: AnchorId.DepartmentsTable,
          spotlightAnchor: AnchorId.DepartmentsTable,
          title: 'TUTORIAL.ORG_DEPARTMENTS.TABLE.TITLE',
          content: 'TUTORIAL.ORG_DEPARTMENTS.TABLE.CONTENT',
          placement: 'top',
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="departments-table"]'),
        },
        {
          id: 'row-actions',
          anchor: AnchorId.DepartmentsColActions,
          spotlightAnchor: AnchorId.DepartmentsColActions,
          title: 'TUTORIAL.ORG_DEPARTMENTS.ROW_ACTIONS.TITLE',
          content: 'TUTORIAL.ORG_DEPARTMENTS.ROW_ACTIONS.CONTENT',
          placement: 'left',
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="departments-col-actions"]'),
        },
        {
          id: 'why',
          anchor: AnchorId.OrganizationView,
          spotlightAnchor: AnchorId.OrganizationView,
          title: 'TUTORIAL.ORG_DEPARTMENTS.WHY.TITLE',
          content: 'TUTORIAL.ORG_DEPARTMENTS.WHY.CONTENT',
          placement: 'bottom',
          nextCondition: 'manual',
          showNextButton: true,
        },
      ],
    },
  ],
};
